import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { prepareImageForUpload, validateImageFile } from "./imageUpload";
import "./meals.css";
import "./meal-photo-fix.css";

type MealType = "breakfast" | "lunch" | "dinner" | "snack" | "other";
type Confidence = "low" | "medium" | "high";
type PeriodFilter = "all" | "today" | "7days" | "30days";

type RawMeal = {
  id: string;
  occurred_at: string;
  meal_type: string;
  title: string;
  notes: string | null;
  calories_kcal: number | null;
  protein_g: number | null;
  fat_g: number | null;
  carbs_g: number | null;
  estimate_min_kcal: number | null;
  estimate_max_kcal: number | null;
  confidence: Confidence | null;
  source: "manual" | "gpt" | "import";
  created_at: string;
};

type MediaRecord = {
  id: string;
  fileKey: string;
  category: string;
  filename: string | null;
  mimeType: string | null;
  fileSize: number | null;
  relatedType: string | null;
  relatedId: string | null;
  createdAt: string;
};

type MealRecord = {
  id: string;
  occurredAt: string;
  mealType: MealType;
  title: string;
  notes: string | null;
  caloriesKcal: number | null;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  estimateMinKcal: number | null;
  estimateMaxKcal: number | null;
  confidence: Confidence | null;
  source: "manual" | "gpt" | "import";
  createdAt: string;
  photoFileKey: string | null;
};

type MealForm = {
  occurredAt: string;
  mealType: MealType;
  title: string;
  notes: string;
  caloriesKcal: string;
  proteinG: string;
  fatG: string;
  carbsG: string;
  estimateMinKcal: string;
  estimateMaxKcal: string;
  confidence: "" | Confidence;
};

const mealTypeLabels: Record<MealType, string> = {
  breakfast: "朝食",
  lunch: "昼食",
  dinner: "夕食",
  snack: "間食",
  other: "その他",
};

const confidenceLabels: Record<Confidence, string> = {
  low: "低め",
  medium: "標準",
  high: "高め",
};

function localDateTimeValue(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function initialForm(): MealForm {
  const hour = new Date().getHours();
  const mealType: MealType = hour < 10 ? "breakfast" : hour < 15 ? "lunch" : hour < 21 ? "dinner" : "snack";
  return {
    occurredAt: localDateTimeValue(),
    mealType,
    title: "",
    notes: "",
    caloriesKcal: "",
    proteinG: "",
    fatG: "",
    carbsG: "",
    estimateMinKcal: "",
    estimateMaxKcal: "",
    confidence: "",
  };
}

function nullableNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMeal(raw: RawMeal, photoFileKey: string | null = null): MealRecord {
  const mealType = Object.prototype.hasOwnProperty.call(mealTypeLabels, raw.meal_type)
    ? raw.meal_type as MealType
    : "other";
  return {
    id: raw.id,
    occurredAt: raw.occurred_at,
    mealType,
    title: raw.title,
    notes: raw.notes,
    caloriesKcal: raw.calories_kcal,
    proteinG: raw.protein_g,
    fatG: raw.fat_g,
    carbsG: raw.carbs_g,
    estimateMinKcal: raw.estimate_min_kcal,
    estimateMaxKcal: raw.estimate_max_kcal,
    confidence: raw.confidence,
    source: raw.source,
    createdAt: raw.created_at,
    photoFileKey,
  };
}

function photoUrl(fileKey: string): string {
  return `/api/app/media/${encodeURIComponent(fileKey)}`;
}

function dateKey(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function displayDate(value: string): string {
  const date = new Date(`${value}T00:00:00+09:00`);
  return new Intl.DateTimeFormat("ja-JP", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(date);
}

function displayTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isWithinPeriod(value: string, period: PeriodFilter): boolean {
  if (period === "all") return true;
  const occurred = new Date(value);
  if (Number.isNaN(occurred.getTime())) return false;
  const now = new Date();
  if (period === "today") return dateKey(value) === dateKey(now.toISOString());
  const days = period === "7days" ? 7 : 30;
  return occurred.getTime() >= now.getTime() - days * 24 * 60 * 60 * 1000;
}

function formatNumber(value: number | null, suffix: string): string {
  return value === null ? "—" : `${Math.round(value * 10) / 10}${suffix}`;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function MealsPage() {
  const [meals, setMeals] = useState<MealRecord[]>([]);
  const [form, setForm] = useState<MealForm>(initialForm);
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [period, setPeriod] = useState<PeriodFilter>("30days");
  const [mealTypeFilter, setMealTypeFilter] = useState<"all" | MealType>("all");
  const [showForm, setShowForm] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [attachingMealId, setAttachingMealId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const previewUrl = useMemo(() => file ? URL.createObjectURL(file) : null, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const loadMeals = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [mealResult, mediaResult] = await Promise.all([
        api<{ ok: true; meals: RawMeal[] }>("/api/app/meals?limit=200"),
        api<{ ok: true; media: MediaRecord[] }>("/api/app/media?category=meal-images"),
      ]);
      const photoByMeal = new Map<string, string>();
      for (const item of mediaResult.media) {
        if (item.relatedType === "meal" && item.relatedId && !photoByMeal.has(item.relatedId)) {
          photoByMeal.set(item.relatedId, item.fileKey);
        }
      }
      setMeals(mealResult.meals.map((meal) => normalizeMeal(meal, photoByMeal.get(meal.id) ?? null)));
    } catch (err) {
      setError(errorMessage(err, "食事記録を読み込めませんでした。"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMeals();
  }, [loadMeals]);

  const visibleMeals = useMemo(
    () => meals.filter((meal) =>
      isWithinPeriod(meal.occurredAt, period) &&
      (mealTypeFilter === "all" || meal.mealType === mealTypeFilter),
    ),
    [mealTypeFilter, meals, period],
  );

  const groups = useMemo(() => {
    const grouped = new Map<string, MealRecord[]>();
    for (const meal of visibleMeals) {
      const key = dateKey(meal.occurredAt);
      const current = grouped.get(key) ?? [];
      current.push(meal);
      grouped.set(key, current);
    }
    return Array.from(grouped.entries());
  }, [visibleMeals]);

  const visibleTotal = useMemo(
    () => visibleMeals.reduce((sum, meal) => sum + (meal.caloriesKcal ?? 0), 0),
    [visibleMeals],
  );

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    setError("");
    setMessage("");
    const selected = event.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      return;
    }
    try {
      validateImageFile(selected);
      setFile(selected);
    } catch (err) {
      setError(errorMessage(err, "写真を選択できませんでした。"));
      event.target.value = "";
    }
  }

  async function uploadMealPhoto(mealId: string, selected: File): Promise<MediaRecord> {
    const prepared = await prepareImageForUpload(selected);
    const photoForm = new FormData();
    photoForm.set("file", prepared);
    photoForm.set("filename", prepared.name);
    photoForm.set("category", "meal-images");
    photoForm.set("relatedType", "meal");
    photoForm.set("relatedId", mealId);
    const result = await api<{ ok: true; media: MediaRecord }>("/api/app/media", {
      method: "POST",
      body: photoForm,
    });
    return result.media;
  }

  async function saveMeal(event: FormEvent) {
    event.preventDefault();
    setError("");
    setMessage("");
    if (!form.title.trim()) {
      setError("食事名を入力してください。");
      return;
    }
    const min = nullableNumber(form.estimateMinKcal);
    const max = nullableNumber(form.estimateMaxKcal);
    if (min !== null && max !== null && min > max) {
      setError("推定範囲は最小値が最大値以下になるよう入力してください。");
      return;
    }

    setSaving(true);
    try {
      const mealResult = await api<{ ok: true; meal: RawMeal }>("/api/app/meals", {
        method: "POST",
        body: JSON.stringify({
          occurredAt: new Date(form.occurredAt).toISOString(),
          mealType: form.mealType,
          title: form.title.trim(),
          notes: form.notes.trim() || null,
          caloriesKcal: nullableNumber(form.caloriesKcal),
          proteinG: nullableNumber(form.proteinG),
          fatG: nullableNumber(form.fatG),
          carbsG: nullableNumber(form.carbsG),
          estimateMinKcal: min,
          estimateMaxKcal: max,
          confidence: form.confidence || null,
        }),
      });

      let created = normalizeMeal(mealResult.meal);
      let photoFailure = "";
      if (file) {
        setMessage("食事を保存しました。写真を軽量化してpCloudへ保存しています…");
        try {
          const media = await uploadMealPhoto(created.id, file);
          created = { ...created, photoFileKey: media.fileKey };
        } catch (err) {
          photoFailure = errorMessage(err, "写真を保存できませんでした。");
        }
      }

      setMeals((current) => [created, ...current]);
      setForm(initialForm());
      setFile(null);
      setInputKey((current) => current + 1);
      setPeriod("30days");
      setMealTypeFilter("all");
      setMessage(photoFailure ? "" : "食事を記録しました。");
      if (photoFailure) {
        setError(`食事は保存されましたが、写真だけ保存できませんでした。${photoFailure} 下の記録一覧にある「写真を追加」から、食事を重複登録せずに再送できます。`);
      }
    } catch (err) {
      setError(errorMessage(err, "食事を記録できませんでした。"));
    } finally {
      setSaving(false);
    }
  }

  async function attachPhoto(meal: MealRecord, event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!selected) return;
    setError("");
    setMessage("");
    setAttachingMealId(meal.id);
    try {
      validateImageFile(selected);
      setMessage(`「${meal.title}」の写真を軽量化して保存しています…`);
      const media = await uploadMealPhoto(meal.id, selected);
      setMeals((current) => current.map((item) =>
        item.id === meal.id ? { ...item, photoFileKey: media.fileKey } : item,
      ));
      setMessage("食事写真を追加しました。");
    } catch (err) {
      setMessage("");
      setError(`写真を追加できませんでした。${errorMessage(err, "時間をおいてもう一度お試しください。")}`);
    } finally {
      setAttachingMealId(null);
    }
  }

  async function removeMeal(meal: MealRecord) {
    if (!window.confirm(`「${meal.title}」の食事記録を削除しますか？\n写真は写真タブに残ります。`)) return;
    setDeleting(meal.id);
    setError("");
    setMessage("");
    try {
      await api(`/api/app/meals/${encodeURIComponent(meal.id)}`, { method: "DELETE" });
      setMeals((current) => current.filter((item) => item.id !== meal.id));
      setMessage("食事記録を削除しました。写真は写真タブに残しています。");
    } catch (err) {
      setError(errorMessage(err, "食事記録を削除できませんでした。"));
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <section className="panel meals-summary">
        <div>
          <p className="eyebrow">MEAL LOG</p>
          <h2>食事記録</h2>
          <p>一食だけの記録でも十分です。空いた日を埋める必要はありません。</p>
        </div>
        <div className="meals-summary-numbers">
          <strong>{visibleMeals.length}</strong>
          <span>件・{Math.round(visibleTotal)} kcal</span>
        </div>
      </section>

      <section className="panel meals-entry">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">QUICK RECORD</p>
            <h2>食事を追加</h2>
          </div>
          <button className="meals-toggle" type="button" onClick={() => setShowForm((current) => !current)}>
            {showForm ? "閉じる" : "入力する"}
          </button>
        </div>

        {showForm && (
          <form className="meals-form" onSubmit={saveMeal}>
            <div className="meals-form-grid">
              <label className="field">
                <span>日時</span>
                <input type="datetime-local" value={form.occurredAt} onChange={(event) => setForm({ ...form, occurredAt: event.target.value })} required />
              </label>
              <label className="field">
                <span>区分</span>
                <select value={form.mealType} onChange={(event) => setForm({ ...form, mealType: event.target.value as MealType })}>
                  {(Object.keys(mealTypeLabels) as MealType[]).map((type) => <option key={type} value={type}>{mealTypeLabels[type]}</option>)}
                </select>
              </label>
              <label className="field meals-wide">
                <span>食事名</span>
                <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="例：鮭おにぎりと味噌汁" maxLength={160} required />
              </label>
              <label className="field">
                <span>カロリー（kcal）</span>
                <input type="number" min="0" step="1" value={form.caloriesKcal} onChange={(event) => setForm({ ...form, caloriesKcal: event.target.value })} placeholder="未入力でも可" />
              </label>
              <label className="field">
                <span>確信度</span>
                <select value={form.confidence} onChange={(event) => setForm({ ...form, confidence: event.target.value as MealForm["confidence"] })}>
                  <option value="">未設定</option><option value="high">高め</option><option value="medium">標準</option><option value="low">低め</option>
                </select>
              </label>
              <label className="field"><span>たんぱく質 P（g）</span><input type="number" min="0" step="0.1" value={form.proteinG} onChange={(event) => setForm({ ...form, proteinG: event.target.value })} /></label>
              <label className="field"><span>脂質 F（g）</span><input type="number" min="0" step="0.1" value={form.fatG} onChange={(event) => setForm({ ...form, fatG: event.target.value })} /></label>
              <label className="field"><span>炭水化物 C（g）</span><input type="number" min="0" step="0.1" value={form.carbsG} onChange={(event) => setForm({ ...form, carbsG: event.target.value })} /></label>
              <div className="meals-estimate field">
                <span>推定カロリー範囲</span>
                <div>
                  <input type="number" min="0" step="1" value={form.estimateMinKcal} onChange={(event) => setForm({ ...form, estimateMinKcal: event.target.value })} placeholder="最小" aria-label="推定最小カロリー" />
                  <span>〜</span>
                  <input type="number" min="0" step="1" value={form.estimateMaxKcal} onChange={(event) => setForm({ ...form, estimateMaxKcal: event.target.value })} placeholder="最大" aria-label="推定最大カロリー" />
                </div>
              </div>
              <label className="field meals-wide">
                <span>メモ</span>
                <textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={3} maxLength={2000} placeholder="量、満足感、外食店など" />
              </label>
              <label className="meals-photo-picker meals-wide">
                <input key={inputKey} type="file" accept="image/*" onChange={chooseFile} />
                <span aria-hidden="true">＋</span>
                <strong>{file ? "写真を変更" : "写真を追加"}</strong>
                <small>カメラ撮影または写真ライブラリ・保存時に自動軽量化</small>
              </label>
              {previewUrl && file && (
                <div className="meals-photo-preview meals-wide">
                  <img src={previewUrl} alt="選択した食事写真のプレビュー" />
                  <div>
                    <strong>{file.name}</strong>
                    <span>{Math.max(1, Math.round(file.size / 1024))} KB・保存時に最適化</span>
                    <button type="button" onClick={() => { setFile(null); setInputKey((current) => current + 1); }}>写真を外す</button>
                  </div>
                </div>
              )}
            </div>
            <button className="button primary" disabled={saving}>{saving ? "保存中…" : "食事を記録"}</button>
          </form>
        )}
      </section>

      {(message || error) && <div className={`notice ${error ? "error" : ""}`}>{error || message}</div>}

      <section className="panel meals-list-panel">
        <div className="panel-heading meals-list-heading">
          <div><p className="eyebrow">HISTORY</p><h2>記録一覧</h2></div>
          <button className="meals-reload" type="button" onClick={() => void loadMeals()} disabled={loading}>更新</button>
        </div>

        <div className="meals-filters" aria-label="表示期間">
          {([["30days", "30日"], ["7days", "7日"], ["today", "今日"], ["all", "すべて"]] as [PeriodFilter, string][]).map(([value, label]) => (
            <button key={value} type="button" className={period === value ? "active" : ""} onClick={() => setPeriod(value)}>{label}</button>
          ))}
        </div>
        <div className="meals-filters" aria-label="食事区分">
          <button type="button" className={mealTypeFilter === "all" ? "active" : ""} onClick={() => setMealTypeFilter("all")}>すべて</button>
          {(Object.keys(mealTypeLabels) as MealType[]).map((type) => (
            <button key={type} type="button" className={mealTypeFilter === type ? "active" : ""} onClick={() => setMealTypeFilter(type)}>{mealTypeLabels[type]}</button>
          ))}
        </div>

        {loading ? (
          <div className="meals-empty">食事記録を読み込んでいます…</div>
        ) : groups.length === 0 ? (
          <div className="meals-empty"><span aria-hidden="true">◒</span><strong>この条件の食事記録はありません</strong><p>一件だけでも記録すると、ここに表示されます。</p></div>
        ) : (
          <div className="meals-groups">
            {groups.map(([date, items]) => {
              const dailyCalories = items.reduce((sum, item) => sum + (item.caloriesKcal ?? 0), 0);
              return (
                <section className="meals-day" key={date}>
                  <header><strong>{displayDate(date)}</strong><span>{items.length}件・{Math.round(dailyCalories)} kcal</span></header>
                  <div className="meals-cards">
                    {items.map((meal) => (
                      <article className="meal-card" key={meal.id}>
                        {meal.photoFileKey ? (
                          <a href={photoUrl(meal.photoFileKey)} target="_blank" rel="noreferrer" className="meal-photo-link">
                            <img src={photoUrl(meal.photoFileKey)} alt={meal.title} loading="lazy" />
                          </a>
                        ) : (
                          <label className={`meal-photo-placeholder meal-photo-add ${attachingMealId === meal.id ? "is-uploading" : ""}`}>
                            <input type="file" accept="image/*" disabled={attachingMealId !== null} onChange={(event) => void attachPhoto(meal, event)} />
                            <span aria-hidden="true">＋</span>
                            <small>{attachingMealId === meal.id ? "保存中…" : "写真を追加"}</small>
                          </label>
                        )}
                        <div className="meal-card-body">
                          <div className="meal-card-topline">
                            <span className="meal-type-chip">{mealTypeLabels[meal.mealType]}</span>
                            <time>{displayTime(meal.occurredAt)}</time>
                            <span className="meal-source-chip">{meal.source === "gpt" ? "GPT" : meal.source === "import" ? "取込" : "手動"}</span>
                          </div>
                          <h3>{meal.title}</h3>
                          <div className="meal-kcal-line">
                            <strong>{formatNumber(meal.caloriesKcal, " kcal")}</strong>
                            {(meal.estimateMinKcal !== null || meal.estimateMaxKcal !== null) && <span>推定 {meal.estimateMinKcal ?? "—"}〜{meal.estimateMaxKcal ?? "—"} kcal</span>}
                            {meal.confidence && <span className={`confidence confidence-${meal.confidence}`}>確信度 {confidenceLabels[meal.confidence]}</span>}
                          </div>
                          <dl className="meal-pfc">
                            <div><dt>P</dt><dd>{formatNumber(meal.proteinG, "g")}</dd></div>
                            <div><dt>F</dt><dd>{formatNumber(meal.fatG, "g")}</dd></div>
                            <div><dt>C</dt><dd>{formatNumber(meal.carbsG, "g")}</dd></div>
                          </dl>
                          {meal.notes && <p className="meal-notes">{meal.notes}</p>}
                          <button className="meal-delete" type="button" onClick={() => void removeMeal(meal)} disabled={deleting === meal.id}>
                            {deleting === meal.id ? "削除中…" : "記録を削除"}
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
